/**
 * Array Input Component for dev-tools
 * Part of Phase 3 refactoring - Component Decomposition (UI Components)
 */
import React from "react";

import { Box, TextField, Typography } from "@mui/material";

import { FieldSchema } from "../../types";

interface ArrayInputProps {
  field: FieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  touched?: boolean;
}

export const ArrayInput: React.FC<ArrayInputProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue.trim() === "") {
      onChange([]);
      return;
    }

    try {
      // Try to parse as JSON first
      const jsonValue = JSON.parse(inputValue);
      if (Array.isArray(jsonValue)) {
        onChange(jsonValue);
      } else {
        // If it's not an array, wrap it in an array
        onChange([jsonValue]);
      }
    } catch {
      // If not valid JSON, split by comma and trim
      const arrayValue = inputValue
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item !== "");
      onChange(arrayValue);
    }
  };

  const displayValue = Array.isArray(value)
    ? JSON.stringify(value, null, 2)
    : String(value || "[]");

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: "600",
          mb: 1,
          color: "text.primary",
          fontSize: "0.9rem",
        }}
      >
        {field.name} (Array)
        {!field.isOptional && " *"}
      </Typography>

      {field.description && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: "block", fontSize: "0.8rem", lineHeight: 1.4 }}
        >
          {field.description}
        </Typography>
      )}

      <TextField
        multiline
        rows={3}
        fullWidth
        name={field.name}
        placeholder="Enter JSON array or comma-separated values"
        value={displayValue}
        onChange={handleChange}
        error={!!error && touched}
        helperText={
          error && touched
            ? error
            : "Enter a JSON array like [1,2,3] or comma-separated values like: value1, value2, value3"
        }
        size="small"
        variant="outlined"
        sx={{
          "& .MuiInputBase-input": {
            fontFamily: "monospace",
            fontSize: "0.875rem",
          },
          "& .MuiFormHelperText-root": {
            margin: "4px 0 0 0",
          },
        }}
      />
    </Box>
  );
};
